import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { useUpdateSitrepV2 } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { getExtentFromGeoJson } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/getExtentFromGeoJson';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import {
  CUSTOM_AREA_LABEL,
  resolveSitrepDefaultSpatialFilter,
} from 'components/Reports/utils/resolveSitrepSpatialFilter';
import { parseAssetRadiusKm } from 'components/Reports/utils/buildDraftSitrepPayload';
import { useAors } from 'hooks/useAors';
import {
  customScopeFromDataSource,
  type EffectiveReportAreaScope,
  type PersistedAreaScopeEntry,
} from './reportAreaScope';

export {
  buildEntityListDataSource,
  customScopeFromDataSource,
  firstSectionAreaScope,
  scopeSourceFromScope,
} from './reportAreaScope';
export type {
  EffectiveReportAreaScope,
  PersistedAreaScopeEntry,
  ReportAreaScopeSource,
} from './reportAreaScope';

type ScopeSitrep = Pick<GetSitrepsResponse, 'aor_id' | 'incident_id'> &
  Partial<
    Pick<
      GetSitrepsResponse,
      | 'id'
      | 'spatial_filter'
      | 'spatial_filter_label'
      | 'scope_source'
      | 'subject_kind'
      | 'asset_name'
      | 'asset_radius_km'
    >
  >;

function sitrepPersistedEntry(
  sitrep?: ScopeSitrep | null
): PersistedAreaScopeEntry | null {
  if (!sitrep?.spatial_filter) return null;
  return {
    spatialFilter: sitrep.spatial_filter,
    spatialFilterLabel: sitrep.spatial_filter_label ?? undefined,
    scopeSource: sitrep.scope_source ?? undefined,
  };
}

export function useReportAreaScope(params: {
  sitrep?: ScopeSitrep | null;
  sitrepId: string;
  legacyPersistedEntry?: PersistedAreaScopeEntry | null;
  persist?: boolean;
}) {
  const { sitrep, sitrepId, legacyPersistedEntry, persist = true } = params;
  const { data: aors = [] } = useAors();
  const { data: incidents = [] } = useAllIncidents();
  const mapCtx = useOptionalUscgMapContext();
  const updateSitrep = useUpdateSitrepV2();
  const hydratedRef = useRef(false);
  const [customScope, setCustomScope] = useState(() =>
    customScopeFromDataSource(
      sitrepPersistedEntry(sitrep) || legacyPersistedEntry
    )
  );
  const [isEditingArea, setIsEditingArea] = useState(false);

  const defaultScope = useMemo(() => {
    if (!sitrep) return null;
    const incident = sitrep.incident_id
      ? incidents.find((item) => item.id === sitrep.incident_id)
      : null;
    const aor = sitrep.aor_id
      ? aors.find((item) => item.id === sitrep.aor_id)
      : null;
    return resolveSitrepDefaultSpatialFilter({ sitrep, incident, aor });
  }, [aors, incidents, sitrep]);

  const subjectKind = sitrep?.subject_kind ?? null;
  const customReportName =
    subjectKind === 'custom'
      ? sitrep?.spatial_filter_label?.trim() || CUSTOM_AREA_LABEL
      : null;

  const subjectLabel = useMemo(() => {
    if (subjectKind === 'asset') {
      return `Asset: ${sitrep?.asset_name?.trim() || 'Asset'}`;
    }
    if (subjectKind === 'custom') {
      return customReportName === CUSTOM_AREA_LABEL
        ? CUSTOM_AREA_LABEL
        : `${CUSTOM_AREA_LABEL}: ${customReportName}`;
    }
    if (!sitrep?.incident_id) return null;
    const incident = incidents.find((item) => item.id === sitrep.incident_id);
    return `Incident: ${incident?.name?.trim() || 'Incident'}`;
  }, [customReportName, incidents, sitrep, subjectKind]);

  const effectiveScope: EffectiveReportAreaScope | null = useMemo(() => {
    if (customScope) {
      return { ...customScope, source: 'custom' };
    }
    return defaultScope;
  }, [customScope, defaultScope]);

  useEffect(() => {
    const fromSitrep = customScopeFromDataSource(sitrepPersistedEntry(sitrep));
    if (fromSitrep) {
      setCustomScope(fromSitrep);
      return;
    }
    if (sitrep?.spatial_filter) {
      setCustomScope(null);
    }
  }, [
    sitrep?.spatial_filter,
    sitrep?.spatial_filter_label,
    sitrep?.scope_source,
  ]);

  useEffect(() => {
    if (!persist || hydratedRef.current || !sitrepId) return;
    if (sitrepPersistedEntry(sitrep)) {
      hydratedRef.current = true;
      return;
    }
    const legacy = customScopeFromDataSource(legacyPersistedEntry);
    if (!legacy) {
      hydratedRef.current = true;
      return;
    }
    hydratedRef.current = true;
    setCustomScope(legacy);
    updateSitrep.mutate({
      sitrep_id: sitrepId,
      spatial_filter: legacy.spatialFilter,
      spatial_filter_label: legacy.spatialFilterLabel,
      scope_source: 'custom',
    });
  }, [legacyPersistedEntry, persist, sitrep, sitrepId, updateSitrep]);

  useEffect(() => {
    if (!isEditingArea || !mapCtx?.registerAiDrawnAreaHandler) {
      return undefined;
    }
    mapCtx.registerAiDrawnAreaHandler((payload) => {
      // A custom-area report keeps its name as the area label.
      const label = customReportName ?? CUSTOM_AREA_LABEL;
      setCustomScope({
        spatialFilter: payload.spatialFilter,
        spatialFilterLabel: label,
      });
      setIsEditingArea(false);
      mapCtx.setAiLayerPickMode(false);
      if (persist) {
        updateSitrep.mutate({
          sitrep_id: sitrepId,
          spatial_filter: payload.spatialFilter,
          spatial_filter_label: label,
          scope_source: 'custom',
        });
      }
      toast.success('Updated report area');
    });
    mapCtx.registerAiDrawnAreaCancelHandler?.(() => {
      setIsEditingArea(false);
      mapCtx.setAiLayerPickMode(false);
    });
    return () => {
      mapCtx.registerAiDrawnAreaHandler(null);
      mapCtx.registerAiDrawnAreaCancelHandler?.(null);
    };
  }, [
    customReportName,
    isEditingArea,
    mapCtx,
    persist,
    sitrepId,
    updateSitrep,
  ]);

  const showReportGeofence = mapCtx?.showReportGeofence;
  const clearReportGeofence = mapCtx?.clearReportGeofence;

  useEffect(() => {
    if (!showReportGeofence || !clearReportGeofence) {
      return undefined;
    }
    if (!effectiveScope?.spatialFilter) {
      clearReportGeofence(sitrepId);
      return undefined;
    }
    showReportGeofence(
      effectiveScope.spatialFilter,
      effectiveScope.spatialFilterLabel,
      sitrepId
    );
    return () => {
      clearReportGeofence(sitrepId);
    };
  }, [
    clearReportGeofence,
    effectiveScope?.spatialFilter,
    effectiveScope?.spatialFilterLabel,
    sitrepId,
    showReportGeofence,
  ]);

  const handleEditOnMap = () => {
    if (
      !mapCtx?.registerAiDrawnAreaHandler ||
      !mapCtx.setAiLayerPickMode ||
      !mapCtx.startAiAreaDraw
    ) {
      toast.info('Open the map to edit the report area');
      return;
    }
    mapCtx.setIsMapVisible(true);
    mapCtx.setAiLayerPickMode(true);
    setIsEditingArea(true);
    mapCtx.registerAiDrawnAreaCancelHandler?.(() => {
      setIsEditingArea(false);
      mapCtx.setAiLayerPickMode(false);
    });
    if (effectiveScope?.spatialFilter) {
      mapCtx.showReportGeofence?.(
        effectiveScope.spatialFilter,
        effectiveScope.spatialFilterLabel,
        sitrepId
      );
    }
    mapCtx.startAiAreaDraw();
  };

  const handleZoomToScope = () => {
    if (!effectiveScope?.spatialFilter) return;
    if (!mapCtx?.setIsMapVisible || !mapCtx.mapView) {
      toast.info('Open the map to view the report area');
      return;
    }
    mapCtx.setIsMapVisible(true);
    mapCtx.showReportGeofence?.(
      effectiveScope.spatialFilter,
      effectiveScope.spatialFilterLabel,
      sitrepId
    );
    const extent = getExtentFromGeoJson(effectiveScope.spatialFilter);
    if (!extent) return;
    void mapCtx.mapView.goTo(extent, { duration: 500 }).catch(() => {});
  };

  const handleAssetRadiusChange = (radiusKm: number) => {
    if (subjectKind !== 'asset' || !persist) return;
    setCustomScope(null);
    updateSitrep.mutate(
      { sitrep_id: sitrepId, asset_radius_km: radiusKm },
      { onSuccess: () => toast.success('Updated asset area') }
    );
  };

  const handleResetScope = () => {
    if (subjectKind === 'custom') return;
    setCustomScope(null);
    setIsEditingArea(false);
    mapCtx?.setAiLayerPickMode(false);
    mapCtx?.cancelAiAreaDraw?.();
    if (persist) {
      updateSitrep.mutate({
        sitrep_id: sitrepId,
        clear_spatial_filter: true,
      });
    }
  };

  return {
    customScope,
    defaultScope,
    effectiveScope,
    subjectLabel,
    subjectKind,
    assetRadiusKm: parseAssetRadiusKm(sitrep?.asset_radius_km),
    isEditingArea,
    handleEditOnMap,
    handleAssetRadiusChange,
    handleCancelDraw: () => mapCtx?.cancelAiAreaDraw?.(),
    handleZoomToScope,
    handleResetScope,
  };
}
